import type { FlowType, InvoiceStatus, VoidKind } from './types';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/** Thrown on non-2xx responses; carries the HTTP status so callers can branch on 401. */
export class ApiError extends Error {
  status: number;
  code?: string;
  stage?: string;
  incidentId?: string;
  constructor(
    status: number,
    message: string,
    details: { code?: string; stage?: string; incidentId?: string } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = details.code;
    this.stage = details.stage;
    this.incidentId = details.incidentId;
  }
}

async function errorFromResponse(res: Response): Promise<ApiError> {
  let msg = `HTTP ${res.status}`;
  let details: { code?: string; stage?: string; incidentId?: string } = {};
  try {
    const errBody = (await res.json()) as {
      error?: string;
      code?: string;
      stage?: string;
      incidentId?: string;
    };
    if (errBody?.error) msg = errBody.error;
    details = {
      code: errBody?.code,
      stage: errBody?.stage,
      incidentId: errBody?.incidentId,
    };
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, msg, details);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}/api${path}`, {
    method,
    credentials: 'include', // send/receive the session cookie
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const get = <T>(path: string) => request<T>('GET', path);
const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body);
const patch = <T>(path: string, body?: unknown) => request<T>('PATCH', path, body);
const del = <T>(path: string) => request<T>('DELETE', path);

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

/* ─── Response shapes ───────────────────────────────────────── */
export interface ClientRow {
  id: string;
  name: string;
  type: 'express' | 'telemed';
  status: 'active' | 'paused' | 'churned';
  currency: string;
  minPatients: number;
  baseFeeCents: number;
  perPatientCents: number;
  pharmacyCount: number;
  fees: { platform: number; pharmacy: number; shipping: number; drNetwork: number };
  outstandingCents: number;
  openInvoiceCount: number;
  /** Flat per-fill pharmacy rate (cents); null = per-drug pricing. */
  pharmacyFlatCents: number | null;
  /** Payment terms in days (invoice → due). Default 3. */
  pharmacyTermsDays: number;
  /** Auto-charge on the due date (extended terms) vs the Monday after invoicing. */
  pharmacyChargeOnDueDate: boolean;
}

/** Minimal client identity returned to dropdown/list selectors. */
export interface ClientOption {
  id: string;
  name: string;
  type: 'express' | 'telemed';
  status: 'active' | 'paused' | 'churned';
}

export interface PricingClientOption extends ClientOption {
  pharmacyFlatCents: number | null;
  pharmacyTermsDays: number;
  pharmacyChargeOnDueDate: boolean;
  /** Cents per patient the physician network saw; null = no dr network fee. */
  drNetworkCents: number | null;
  /** Pharmacy runs issue one invoice per calendar month of activity. */
  monthlySplitInvoices: boolean;
  /** weekly = Sunday run; monthly = one invoice per calendar month, on the 1st. */
  pharmacyCadence: PharmacyCadence;
}

export type PharmacyCadence = 'weekly' | 'monthly';

export type CollectionMethod = 'auto_charge' | 'wire' | 'ach';

export interface InvoiceRow {
  _id: string;
  clientId: string;
  clientName: string;
  flow: FlowType;
  status: InvoiceStatus;
  collectionMethod: CollectionMethod;
  invoiceNumber?: string;
  currency: string;
  period: { label: string };
  totalCents: number;
  amountDueCents: number;
  amountPaidCents: number;
  dueAt?: string;
  issuedAt?: string;
  voidKind?: VoidKind;
}

export interface NewInvoiceLine {
  description: string;
  quantity?: number;
  unitPriceCents: number;
}
export interface NewInvoice {
  clientId: string;
  lineItems: NewInvoiceLine[];
  collectionMethod?: CollectionMethod;
  dueAt?: string;
  periodLabel?: string;
  /** Create in the review (draft) state instead of issuing immediately. */
  draft?: boolean;
}

export interface PricingRow {
  id: string;
  clientId: string | null;
  drug: string;
  form: string;
  strength?: string;
  size?: string;
  unit: string;
  costCents: number;
  priceCents: number;
  currency: string;
  scope: 'client' | 'global';
}

export interface NewPricing {
  clientId?: string | null;
  drug: string;
  form: string;
  strength?: string;
  size?: string;
  unit?: string;
  costCents: number;
  priceCents: number;
}

export interface NewClient {
  name: string;
  type: 'express' | 'telemed';
  currency?: string;
  coreAccountId?: string;
  pharmacyIds?: string[];
  platformPlan?: { minPatients: number; baseFeeCents: number; perPatientCents: number; billingDayOfMonth?: number };
}

export interface Summary {
  byFlow: { flow: FlowType; totalCents: number }[];
  outstandingCents: number;
  openInvoiceCount: number;
  collectedThisMonthCents: number;
  clientCount: number;
  recentInvoices: {
    _id: string;
    flow: FlowType;
    status: InvoiceStatus;
    totalCents: number;
    dueAt?: string;
    period: { label: string };
    clientName: string;
  }[];
  upcomingRuns: { flow: FlowType; rails: string; nextRun: string }[];
}

/* ─── Auth & users ──────────────────────────────────────────── */
export type UserRole = 'super_admin' | 'accounting' | 'clinic_view';
export type Permission = string;

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  clientId: string | null;
  permissions: Permission[];
}

export interface ManagedUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  clientId: string | null;
  status: 'active' | 'disabled';
  lastLoginAt: string | null;
  createdAt: string;
}

export interface NewUser {
  email: string;
  name: string;
  /** Omit to invite the user by email (they set their own password). */
  password?: string;
  role: UserRole;
  clientId?: string;
}

export interface UserPatch {
  name?: string;
  role?: UserRole;
  status?: 'active' | 'disabled';
  clientId?: string | null;
  password?: string;
}

/* ─── Endpoints ─────────────────────────────────────────────── */
export const getClients = () => get<{ clients: ClientRow[] }>('/clients');
export const getClientOptions = () => get<{ clients: ClientOption[] }>('/clients/options');
export const getPricingClientOptions = () =>
  get<{ clients: PricingClientOption[] }>('/clients/options/pricing');
export const createClient = (body: NewClient) =>
  post<{ client: { id: string; name: string; type: string; status: string } }>('/clients', body);
export interface UnlinkedClinic {
  clinic: string;
  orders: number;
  completed: number;
}
/** pharmacyV2 clinics with orders but no billing client yet (staff). */
export const getUnlinkedClinics = () => get<{ clinics: UnlinkedClinic[] }>('/clients/unlinked-clinics');
/** Update a client's billing config: pharmacy flat rate, terms, charge timing,
 *  the dr network per-patient rate, and monthly invoice splitting. */
export const updateClientPharmacy = (
  id: string,
  body: {
    pharmacyFlatCents?: number | null;
    pharmacyTermsDays?: number;
    pharmacyChargeOnDueDate?: boolean;
    drNetworkCents?: number | null;
    monthlySplitInvoices?: boolean;
    pharmacyCadence?: PharmacyCadence;
  },
) =>
  patch<{
    ok: true;
    pharmacyFlatCents: number | null;
    pharmacyTermsDays: number;
    pharmacyChargeOnDueDate: boolean;
    drNetworkCents: number | null;
    monthlySplitInvoices: boolean;
    pharmacyCadence: PharmacyCadence;
  }>(`/clients/${id}`, body);
/** Set/clear a client's flat per-fill pharmacy rate (cents; null = per-drug). */
export const setClientFlatRate = (id: string, pharmacyFlatCents: number | null) =>
  updateClientPharmacy(id, { pharmacyFlatCents });
/** Update a client's lifecycle status. Only `active` clients are billed. */
export const updateClientStatus = (id: string, status: 'active' | 'paused' | 'churned') =>
  patch<{ ok: true; status: 'active' | 'paused' | 'churned' }>(`/clients/${id}/status`, { status });
export const getInvoices = (params?: { flow?: FlowType; status?: InvoiceStatus; clientId?: string; limit?: number }) =>
  get<{ invoices: InvoiceRow[] }>(`/invoices${qs(params)}`);
export const createInvoice = (body: NewInvoice) =>
  post<{ invoice: { id: string; invoiceNumber?: string; status: InvoiceStatus } }>('/invoices', body);
/** Finalize a reviewed draft: assign its number, stamp the issue date, and
 *  compute due/charge dates from client terms. Draft → open. */
export const finalizeInvoice = (id: string) =>
  post<{ id: string; status: string; invoiceNumber?: string; dueAt?: string; chargeAt?: string }>(
    `/invoices/${id}/finalize`,
    {},
  );
export const markInvoicePaid = (
  id: string,
  body: { amountCents?: number; method?: string; reference?: string; paidAt?: string },
) => post<{ status: string; amountPaidCents: number; amountDueCents: number }>(`/invoices/${id}/mark-paid`, body);
export const updateInvoice = (
  id: string,
  body: { collectionMethod?: CollectionMethod; status?: 'void'; voidReason?: string; voidKind?: VoidKind; dueAt?: string },
) =>
  patch<{ ok: true }>(`/invoices/${id}`, body);
/** Refund a paid invoice via its rail (full by default, or a partial amountCents). */
export const refundInvoice = (id: string, body: { amountCents?: number; reason?: string }) =>
  post<{ status: string; amountDueCents: number }>(`/invoices/${id}/refund`, body);
export const invoicePdfUrl = (id: string) => `${BASE}/api/invoices/${id}/pdf`;
export const invoiceSupportingCsvUrl = (id: string) => `${BASE}/api/invoices/${id}/supporting.csv`;
/** Add a credit (negative) or fee (positive) line to an unpaid invoice; a partially paid one takes credits up to its balance. */
export const addInvoiceAdjustment = (id: string, body: { description: string; amountCents: number }) =>
  post<{ ok: true; totalCents: number; amountDueCents: number }>(`/invoices/${id}/adjustments`, body);
/** Auto-charge all due invoices now (staff). */
export const runChargeDue = () =>
  post<{ due: number; charged: number; skipped: number; failed: number }>('/billing/charge-due', {});

export const getPricing = (params?: { clientId?: string; scope?: 'global' }) =>
  get<{ pricing: PricingRow[] }>(`/pricing${qs(params)}`);
export const createPricing = (body: NewPricing) => post<{ pricing: PricingRow }>('/pricing', body);
export const updatePricing = (id: string, body: { costCents?: number; priceCents?: number; unit?: string }) =>
  patch<{ pricing: PricingRow }>(`/pricing/${id}`, body);
export const deletePricing = (id: string) => del<{ ok: true }>(`/pricing/${id}`);

export const getSummary = () => get<Summary>('/billing/summary');

/* ─── Client detail ─────────────────────────────────────────── */
export interface ClientDetail {
  client: {
    id: string;
    name: string;
    legalName: string | null;
    type: 'express' | 'telemed';
    status: 'active' | 'paused' | 'churned';
    currency: string;
    pharmacyIds: string[];
    coreAccountId: string | null;
    pharmacyFlatCents: number | null;
    pharmacyTermsDays: number;
    pharmacyChargeOnDueDate: boolean;
    /** Cents per patient the physician network saw; null = no dr network fee. */
    drNetworkCents: number | null;
    /** Pharmacy runs issue one invoice per calendar month of activity. */
    monthlySplitInvoices: boolean;
    pharmacyCadence: PharmacyCadence;
    platformPlan: { minPatients: number; baseFeeCents: number; perPatientCents: number; billingDayOfMonth: number } | null;
    hasCardLink: boolean;
    invoiceRecipients: string[];
    aggressiveReminders: boolean;
    attachedDetail: boolean;
  };
  fees: { platform: number; pharmacy: number; shipping: number; drNetwork: number };
  outstandingCents: number;
  openInvoiceCount: number;
  accruedCents: number;
  accrued: { pharmacyCents: number; shippingCents: number } | null;
  cards: CardOverviewRail[];
}
export const getClient = (id: string) => get<ClientDetail>(`/clients/${id}`);
/** Replace the client's invoice-recipient email list. */
export const updateClientRecipients = (id: string, invoiceRecipients: string[]) =>
  patch<{ ok: true; invoiceRecipients: string[] }>(`/clients/${id}/invoice-recipients`, { invoiceRecipients });

export interface ClientEmailFlags {
  aggressiveReminders: boolean;
  attachedDetail: boolean;
}
/** Toggle one client's email feature flags. Send only the flag being changed. */
export const updateClientEmailFlags = (id: string, flags: Partial<ClientEmailFlags>) =>
  patch<{ ok: true } & ClientEmailFlags>(`/clients/${id}/email-flags`, flags);

/* ─── Invoice detail ────────────────────────────────────────── */
export interface InvoiceLine {
  kind: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
  meta?: Record<string, unknown>;
}
export interface InvoicePayment {
  id: string;
  provider: string;
  providerPaymentId?: string;
  amountCents: number;
  currency: string;
  status: string;
  failureReason?: string;
  attemptedAt: string;
  settledAt?: string;
  /** Offline payments: collection method + confirmation/reference #. */
  method?: string | null;
  reference?: string | null;
}

/** Invoices at/above this total are wire/ACH only — no card charge or pay link. */
export const CARD_PAYMENT_MAX_CENTS = 2_000_000; // $20,000

/** Mirrors EMAILABLE_FLOWS in server/src/domain/enums.ts. */
export const EMAILABLE_FLOWS: FlowType[] = ['pharmacy', 'platform', 'dr_network'];
export interface InvoiceFull extends InvoiceRow {
  id: string;
  legalName?: string;
  lineItems: InvoiceLine[];
  subtotalCents: number;
  taxCents: number;
  chargeAt?: string;
  paidAt?: string;
  voidedAt?: string;
  voidedBy?: string;
  voidReason?: string;
  voidKind?: VoidKind;
}
export const getInvoice = (id: string) => get<{ invoice: InvoiceFull; payments: InvoicePayment[] }>(`/invoices/${id}`);

export type InvoiceEmailTrigger = 'scheduler' | 'finalize' | 'manual_send' | 'manual_retry' | 'manual_resend';
export type InvoiceEmailStatus =
  | 'pending'
  | 'submitting'
  | 'accepted'
  | 'delivered'
  | 'bounced'
  | 'failed'
  | 'uncertain';

export interface InvoiceEmailEvent {
  type: string;
  occurredAt: string;
  detail?: string;
}

export interface InvoiceEmailAttempt {
  id: string;
  recipient: string;
  trigger: InvoiceEmailTrigger;
  requestedBy?: string;
  status: InvoiceEmailStatus;
  attemptedAt: string;
  submittedAt?: string;
  deliveredAt?: string;
  bouncedAt?: string;
  firstClickedAt?: string;
  lastClickedAt?: string;
  failureReason?: string;
  events: InvoiceEmailEvent[];
}

export interface InvoiceEmailRecipientResult {
  recipient: string;
  status: InvoiceEmailStatus;
  attemptId?: string;
  failureReason?: string;
}

export type SendInvoiceEmailMode = 'initial' | 'retry_unsent' | 'resend_all';

/** Staff-only delivery audit for an invoice. Clinic users must not call this endpoint. */
export const getInvoiceEmailAttempts = (id: string) =>
  get<{ attempts: InvoiceEmailAttempt[]; legacyEmailedAt?: string }>(`/invoices/${id}/email-attempts`);

/** Send only unsent/definitively failed recipients, or explicitly resend to everyone. */
export const sendInvoiceEmail = (
  id: string,
  mode: SendInvoiceEmailMode,
  requestId = crypto.randomUUID(),
) => post<{ sent: number; recipients: number; results: InvoiceEmailRecipientResult[] }>(
  `/invoices/${id}/email`,
  { mode, requestId },
);

/* ─── Payments ledger + dunning + revenue ───────────────────── */
export interface PaymentTxn {
  id: string;
  provider: string;
  providerPaymentId?: string;
  amountCents: number;
  currency: string;
  status: string;
  failureReason?: string;
  attemptedAt: string;
  settledAt?: string;
  clientId: string;
  clientName: string;
  invoiceId: string;
  invoiceNumber?: string;
}
export const getPaymentTransactions = (params?: { status?: string; provider?: string; limit?: number }) =>
  get<{ payments: PaymentTxn[] }>(`/payments/transactions${qs(params)}`);

export interface DunningRow {
  id: string;
  invoiceNumber?: string;
  amountDueCents: number;
  dueAt?: string;
  chargeAt?: string;
  rails: string;
  clientId: string;
  clientName: string;
  lastFailure?: string;
}
export const getDunning = () => get<{ invoices: DunningRow[] }>('/reports/dunning');

export interface RevenueMonth {
  month: string;
  label: string;
  invoicedCents: number;
  collectedCents: number;
}
export const getRevenue = (months?: number) => get<{ months: RevenueMonth[] }>(`/reports/revenue${qs({ months })}`);

/* ─── Shipping rates ────────────────────────────────────────── */
export type ShippingService = 'overnight' | '2day' | 'sat_surcharge';
export interface ShippingRateRow {
  id: string;
  clientId: string | null;
  service: ShippingService;
  priceCents: number;
  currency: string;
  effectiveFrom: string;
  scope: 'client' | 'global';
}
export const getShippingRates = (params?: { clientId?: string; scope?: 'global' }) =>
  get<{ rates: ShippingRateRow[] }>(`/shipping-rates${qs(params)}`);
export const setShippingRate = (body: {
  clientId?: string | null;
  service: ShippingService;
  priceCents: number;
  currency?: string;
}) => post<{ rate: ShippingRateRow }>('/shipping-rates', body);
export const deleteShippingRate = (id: string) => del<{ ok: true }>(`/shipping-rates/${id}`);

/** Run pharmacy invoicing (coverage model) for all active clients. Bills
 *  pharmacy + shipping on one invoice per client. With `draft`, invoices are
 *  created for internal review instead of issued live. */
export const runPharmacyBilling = (body?: { asOf?: string; lookbackDays?: number; draft?: boolean }) =>
  post<{ clientCount: number; invoiceCount: number; ordersInvoiced: number; shipmentsInvoiced: number; grossCents: number }>(
    '/billing/pharmacy',
    body ?? {},
  );

export interface DraftedInvoice {
  invoiceId: string;
  orders: number;
  shipments: number;
  totalCents: number;
  /** Set when the client bills on monthly splits: the month this one covers. */
  billingMonth?: string;
}
export interface ClientDraftResult {
  superseded: number;
  /** The combined pharmacy+shipping drafts — one per activity month for a client
   *  on monthly splits, one in total otherwise, empty if there was nothing to bill. */
  invoices: DraftedInvoice[];
}
/** Generate one combined pharmacy+shipping draft invoice for a client, for review.
 *  `supersede` first voids any existing open drafts for the client (releasing
 *  their ledger claims) so the new draft picks those orders/shipments back up. */
export const generateClientDraft = (
  id: string,
  body?: { asOf?: string; lookbackDays?: number; supersede?: boolean },
) => post<ClientDraftResult>(`/billing/clients/${id}/draft`, body ?? {});

/** One-line summary of a per-client draft run for a toast/banner. */
export function draftResultMessage(r: ClientDraftResult): string {
  const prefix = r.superseded > 0 ? `Superseded ${r.superseded} open draft${r.superseded > 1 ? 's' : ''}. ` : '';
  if (r.invoices.length === 0) return `${prefix}Nothing uninvoiced to draft for this client.`;
  const totalCents = r.invoices.reduce((s, i) => s + i.totalCents, 0);
  const orders = r.invoices.reduce((s, i) => s + i.orders, 0);
  const shipments = r.invoices.reduce((s, i) => s + i.shipments, 0);
  const bits = [`${orders} orders`];
  if (shipments > 0) bits.push(`${shipments} shipments`);
  // Monthly splits produce several invoices from one run; say how many so the
  // number of emails about to go out is never a surprise.
  const count = r.invoices.length > 1 ? ` across ${r.invoices.length} monthly invoices` : '';
  return `${prefix}Drafted ${fmtDollars(totalCents)} (${bits.join(', ')})${count} — review, then finalize to send.`;
}
const fmtDollars = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/* ─── Reports ───────────────────────────────────────────────── */
export interface DrugVolume {
  drug: string;
  total: number;
  weekly: Record<string, number>;
}
export interface ClinicVolume {
  clinic: string;
  total: number;
  weekly: Record<string, number>;
  drugs: DrugVolume[];
  pipelineOrders?: number;
}
export interface PharmacyVolume {
  from: string;
  to: string;
  weeks: string[];
  clinics: ClinicVolume[];
  computedAt?: string;
  cached?: boolean;
}
export const getPharmacyVolume = (weeks?: number, fresh?: boolean) =>
  get<PharmacyVolume>(`/reports/pharmacy-volume${qs({ weeks, fresh: fresh ? 1 : undefined })}`);

export interface ClientAccrued {
  clientId: string;
  pharmacyCents: number;
  pharmacyOrders: number;
  shippingCents: number;
  shippingShipments: number;
  totalCents: number;
  unpriced: { drugs: number; shippingClasses: number; shipments: number };
}
/** Live unbilled balances per client (pharmacy + shipping not yet invoiced). */
export const getAccrued = (fresh?: boolean) =>
  get<{ asOf: string; clients: ClientAccrued[]; computedAt?: string; cached?: boolean }>(
    `/reports/accrued${qs({ fresh: fresh ? 1 : undefined })}`,
  );

export interface ArAging {
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90: number;
}
export interface ArClient {
  clientId: string;
  name: string;
  outstandingCents: number;
  openCount: number;
  oldestDueAt: string | null;
  aging: ArAging;
  accruedCents: number;
}
export interface ArReport {
  totals: ArAging & { outstandingCents: number; accruedCents: number; openCount: number };
  clients: ArClient[];
  accruedComputedAt: string | null;
}
/** Accounts receivable: aged outstanding + accrued (unbilled) per client. */
export const getAR = () => get<ArReport>('/reports/ar');

export interface ActivePatients {
  period: string;
  clients: { clientId: string; activePatients: number | null }[];
}
export const getActivePatients = () => get<ActivePatients & { computedAt?: string; cached?: boolean }>('/reports/active-patients');
export type MetricSnapshotKey =
  | 'pharmacy-volume'
  | 'active-patients'
  | 'product-catalog'
  | 'accrued'
  | 'unlinked-clinics';

/** Recompute all cached metrics, or only the named snapshots (staff). */
export const refreshReports = (keys?: MetricSnapshotKey[]) =>
  post<{ snapshots: { key: string; computedAt: string; ok: boolean }[] }>('/reports/refresh', keys ? { keys } : undefined);

export interface DeferredUsageItem {
  id: string;
  usageKey: string;
  clientId: string;
  clientName?: string;
  orderId: string;
  clinic?: string;
  drug: string;
  form: string;
  strength?: string;
  size?: string;
  quantity: number;
  firstSeenAt: string;
  lastSeenAt: string;
  reason?: string;
}

export interface DeferredUsageReport {
  total: number;
  byClient?: { clientId: string; clientName?: string; count: number }[];
  items: DeferredUsageItem[];
}

/** Pharmacy usage held out of invoices until a matching price is available. */
export const getDeferredUsage = (params?: { clientId?: string }) =>
  get<DeferredUsageReport>(`/reports/deferred-usage${qs(params)}`);

export interface CatalogProduct {
  key: string;
  drug: string;
  form: string;
  strength?: string;
  size?: string;
  volume: number;
  sampleName: string;
  globalPriceCents: number | null;
  clientPriceCents: number | null;
  priced: boolean;
  /** 'flat' = client is on a flat per-fill rate, so every product is priced. */
  scope: 'client' | 'global' | 'flat' | 'none';
}
export interface ProductCatalog {
  from: string;
  to: string;
  products: CatalogProduct[];
  coverage: { pricedProducts: number; totalProducts: number; pricedVolume: number; totalVolume: number };
  computedAt?: string;
  cached?: boolean;
}
/** Dispensed products over the window with price-coverage (staff). */
export const getProductCatalog = (params?: { clientId?: string; fresh?: boolean }) =>
  get<ProductCatalog>(`/reports/product-catalog${qs({ clientId: params?.clientId, fresh: params?.fresh ? 1 : undefined })}`);

/* ─── Payments (Stripe) ─────────────────────────────────────── */
export interface SavedCard {
  id: string;
  brand?: string;
  last4?: string;
  expMonth?: number | string;
  expYear?: number | string;
  isDefault?: boolean;
}

export type PaymentRail = 'stripe' | 'hyperswitch';
export interface PaymentConfig {
  stripe: { configured: boolean; publishableKey: string | null };
  hyperswitch: { configured: boolean; publishableKey: string | null };
}

export const getPaymentConfig = () => get<PaymentConfig>('/payments/config');

// Stripe (platform)
export const createSetupIntent = (clientId: string) =>
  post<{ clientSecret: string }>(`/payments/clients/${clientId}/setup-intent`);
export const getPaymentMethods = (clientId: string) =>
  get<{ cards: SavedCard[] }>(`/payments/clients/${clientId}/payment-methods`);

// Hyperswitch (pharmacy)
export const hsCreateSetup = (clientId: string) =>
  post<{ clientSecret: string; publishableKey: string | null }>(`/payments/clients/${clientId}/hyperswitch/setup`);
export const hsGetPaymentMethods = (clientId: string) =>
  get<{ cards: SavedCard[] }>(`/payments/clients/${clientId}/hyperswitch/payment-methods`);

export const chargeInvoice = (invoiceId: string) =>
  post<{ paymentId?: string; paymentIntentId?: string; status: string }>(`/payments/invoices/${invoiceId}/charge`);

/** Pull our Hyperswitch events from bloom's webhook store and reconcile (staff). */
export const reconcileHyperswitch = () =>
  post<{ scanned: number; reconciled: number; failed: number; checkpoint: string | null }>('/payments/reconcile-hyperswitch');

/* ─── Card-on-file overview + add-card link ─────────────────── */
export interface CardSummary {
  brand?: string;
  last4?: string;
  expMonth?: number | string;
  expYear?: number | string;
}
export interface CardOverviewRail {
  rail: PaymentRail;
  hasCustomer: boolean;
  cardCount: number;
  selected: CardSummary | null;
}
export interface CardOverviewClient {
  id: string;
  name: string;
  type: 'express' | 'telemed';
  hasCardLink: boolean;
  rails: CardOverviewRail[];
}
/** Per-client card-on-file status across rails (staff). */
export const getPaymentsOverview = () => get<{ clients: CardOverviewClient[] }>('/payments/overview');
/** Get/create the client's public add-card token. */
export const createCardLink = (clientId: string) => post<{ token: string }>(`/payments/clients/${clientId}/card-link`);

/* ─── Public hosted add-card page ───────────────────────────── */
export interface CardSetupInfo {
  clientName: string;
  rails: { rail: PaymentRail; onFile: boolean; current: CardSummary | null }[];
}
export const getCardSetup = (token: string) => get<CardSetupInfo>(`/card-setup/${token}`);
export const createCardSetupIntent = (token: string, rail: PaymentRail) =>
  post<{ clientSecret: string; publishableKey: string | null; rail: PaymentRail }>(`/card-setup/${token}/intent`, { rail });

/* ─── Hosted invoice pay link ───────────────────────────────── */
export interface PayInvoiceInfo {
  invoiceNumber?: string;
  clientName: string;
  status: InvoiceStatus;
  currency: string;
  totalCents: number;
  amountDueCents: number;
  period: string;
  dueAt?: string;
  lineItems: { description: string; quantity: number; amountCents: number }[];
  payable: boolean;
}
/** Admin: create/return the hosted pay token for an invoice. */
export const createPayLink = (invoiceId: string) => post<{ token: string }>(`/invoices/${invoiceId}/pay-link`);
/** Public: hosted pay page info + payment intent. */
export const getPayInvoice = (token: string) => get<{ invoice: PayInvoiceInfo }>(`/pay/${token}`);
export const createPayIntent = (token: string) =>
  post<{ clientSecret: string; publishableKey: string | null; rail: PaymentRail }>(`/pay/${token}/intent`);

export const login = (email: string, password: string) =>
  post<{ user: AuthUser }>('/auth/login', { email, password });
export const logout = () => post<{ ok: true }>('/auth/logout');
export const getMe = () => get<{ user: AuthUser }>('/auth/me');
/** Change the signed-in user's own password (keeps this session, logs out others). */
export const changePassword = (currentPassword: string, newPassword: string) =>
  post<{ ok: true }>('/auth/change-password', { currentPassword, newPassword });

export const getUsers = () => get<{ users: ManagedUser[] }>('/users');
export const createUser = (body: NewUser) =>
  post<{ user: ManagedUser; invited?: boolean; emailSent?: boolean; inviteUrl?: string }>('/users', body);
export const updateUser = (id: string, body: UserPatch) =>
  patch<{ user: ManagedUser }>(`/users/${id}`, body);
export const deleteUser = (id: string) => del<{ ok: true }>(`/users/${id}`);
/** (Re)send a set-password link to a user. */
export const resendInvite = (id: string) => post<{ emailSent: boolean; inviteUrl?: string }>(`/users/${id}/invite`);

/* ─── FedEx bills (expense audit) ───────────────────────────── */
export interface FedexInvoiceSummary {
  id: string;
  fileName: string;
  fedexInvoiceNumber: string | null;
  accountNumber: string | null;
  invoiceDate: string | null;
  expressTotalCents: number;
  otherChargesCents: number;
  grandTotalCents: number;
  shipmentCount: number;
  status: 'parsed' | 'reconciled';
  reconciledAt: string | null;
  matchedCount: number;
  unmatchedCount: number;
  uploadedByEmail: string | null;
  warnings: string[];
  createdAt: string | null;
}

export interface FedexBrandRow {
  clinic: string | null;
  clientId: string | null;
  clientName?: string;
  shipments: number;
  realCostCents: number;
  chargedCents: number;
  marginCents: number;
  ratedShipments: number;
}

export interface FedexShipmentSla {
  status: 'on_time' | 'late' | 'unknown';
  reason?:
    | 'missing_in_shipping'
    | 'missing_delivery'
    | 'unsupported_service'
    | 'invalid_timestamps';
  inShippingDate: string | null;
  expectedDeliveryDate: string | null;
  deliveredDate: string | null;
  businessDaysLate: number | null;
  ruleVersion: 'weekends_only_v1';
  timeZone: string;
}

export interface FedexChargeRow {
  _id: string;
  trackingNumber?: string;
  custRef?: string;
  shipDate?: string;
  inShippingAt?: string;
  deliveredAt?: string;
  deliveryTimestampSource?: 'status_history' | 'fill_timestamp';
  serviceTypeText?: string;
  automation?: string;
  packageType?: string;
  zone?: string;
  transportationCents?: number;
  discountCents?: number;
  realCostCents: number;
  matched: boolean;
  matchSource?: 'terminal' | 'v2';
  matchStrategy?: 'tracking' | 'fill_id';
  clinic?: string;
  pharmacyOrderId?: string;
  fillId?: string;
  clientId?: string;
  shippedService?: 'overnight' | '2day' | 'sat_overnight';
  chargedCents?: number;
  marginCents?: number;
  billed: boolean;
  flags: string[];
  sla: FedexShipmentSla;
}

export interface FedexInvoiceDetail {
  invoice: FedexInvoiceSummary;
  byBrand: FedexBrandRow[];
  flagCounts: { flag: string; count: number }[];
  slaCounts: { late: number; onTime: number; unknown: number };
  charges: FedexChargeRow[];
}

export interface FedexShipmentDetail {
  invoice: FedexInvoiceSummary;
  charge: FedexChargeRow;
}

export const getFedexInvoices = () => get<{ invoices: FedexInvoiceSummary[] }>('/fedex/invoices');
export const getFedexInvoice = (id: string) => get<FedexInvoiceDetail>(`/fedex/invoices/${id}`);
export const getFedexShipment = (invoiceId: string, chargeId: string) =>
  get<FedexShipmentDetail>(`/fedex/invoices/${invoiceId}/charges/${chargeId}`);
export const reconcileFedexInvoice = (id: string) =>
  post<{ invoice: FedexInvoiceSummary }>(`/fedex/invoices/${id}/reconcile`);
export const deleteFedexInvoice = (id: string) => del<void>(`/fedex/invoices/${id}`);

/** Upload a FedEx bill PDF as a raw body (not JSON). Returns the parsed+reconciled record. */
export async function uploadFedexInvoice(
  file: File,
): Promise<{ invoice: FedexInvoiceSummary; alreadyUploaded?: boolean }> {
  const res = await fetch(`${BASE}/api/fedex/invoices${qs({ filename: file.name })}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/pdf' },
    body: file,
  });
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  return (await res.json()) as { invoice: FedexInvoiceSummary; alreadyUploaded?: boolean };
}

/* ─── Pharmacy payables (accounts payable to 3rd-party pharmacies) ─── */
export type PayableStatus = 'draft' | 'open' | 'paid' | 'void';

export interface PayeeRemit {
  accountName?: string;
  bankName?: string;
  accountNumber?: string;
  routingNumber?: string;
  instructions?: string;
}
export interface PharmacyPayee {
  id: string;
  ncpdpid: string;
  name: string;
  legalName: string | null;
  status: 'active' | 'inactive';
  currency: string;
  termsDays: number;
  remit: PayeeRemit | null;
}

export interface PayableRow {
  id: string;
  payeeId: string;
  payeeName: string;
  ncpdpid: string;
  status: PayableStatus;
  invoiceNumber?: string;
  currency: string;
  period: { label: string };
  totalCents: number;
  amountDueCents: number;
  amountPaidCents: number;
  issuedAt?: string;
  dueAt?: string;
  paidAt?: string;
}
export interface PayableLine {
  description: string;
  quantity: number;
  unitCostCents: number;
  amountCents: number;
  meta?: Record<string, unknown>;
}
export interface PayableFull extends PayableRow {
  lineItems: PayableLine[];
  subtotalCents: number;
  snapshot: Record<string, unknown>;
  requestedBy?: string;
  paidReference?: string;
  createdAt: string;
}

export interface PharmacyCostRow {
  id: string;
  ncpdpid: string;
  drugName: string;
  dispenseQuantity: number;
  dispenseUnit: string;
  costCents: number;
  currency: string;
  label: string | null;
  effectiveFrom: string;
}

export interface PayableSummary {
  owedCents: number;
  openCount: number;
  draftCents: number;
  draftCount: number;
  paidCents: number;
  payeeCount: number;
  byPayee: { ncpdpid: string; owedCents: number; openCount: number }[];
}

export const getPayables = (params?: { payeeId?: string; status?: PayableStatus }) =>
  get<{ payables: PayableRow[] }>(`/payables${qs(params)}`);
export const getPayable = (id: string) =>
  get<{ payable: PayableFull; payee: PharmacyPayee | null }>(`/payables/${id}`);
export const getPayablesSummary = () => get<PayableSummary>('/payables/summary');
/** Run pharmacy payables for all active payees (or just `ncpdpid`). With `draft`,
 *  payables are created for internal review instead of issued. */
export const runPayables = (body?: {
  /** Explicit coverage window [from, to) (ISO) — e.g. a specific week range. */
  from?: string;
  to?: string;
  /** Fallback catch-up window: [asOf-lookbackDays, asOf). Ignored if from/to set. */
  asOf?: string;
  lookbackDays?: number;
  draft?: boolean;
  ncpdpid?: string;
}) =>
  post<{ payeeCount: number; invoiceCount: number; fillsPaid: number; deferred: number; grossCents: number }>(
    '/payables/run',
    body ?? {},
  );
export const finalizePayable = (id: string) =>
  post<{ id: string; status: string; invoiceNumber?: string; dueAt?: string }>(`/payables/${id}/finalize`, {});
export const markPayablePaid = (id: string, body: { amountCents?: number; reference?: string; paidAt?: string }) =>
  patch<{ status: string; amountPaidCents: number; amountDueCents: number }>(`/payables/${id}`, { markPaid: body });
export const voidPayable = (id: string) => patch<{ ok: true; status: string }>(`/payables/${id}`, { status: 'void' });
export const payablePdfUrl = (id: string) => `${BASE}/api/payables/${id}/pdf`;
export const payableSupportingCsvUrl = (id: string) => `${BASE}/api/payables/${id}/supporting.csv`;

export const getPayees = () => get<{ payees: PharmacyPayee[] }>('/payables/payees');
export const savePayee = (body: {
  ncpdpid: string;
  name: string;
  legalName?: string;
  status?: 'active' | 'inactive';
  termsDays?: number;
  remit?: PayeeRemit;
}) => post<{ payee: PharmacyPayee }>('/payables/payees', body);

export const getPharmacyCosts = (params?: { ncpdpid?: string }) =>
  get<{ costs: PharmacyCostRow[] }>(`/payables/costs${qs(params)}`);
export const createPharmacyCost = (body: {
  ncpdpid: string;
  drugName: string;
  dispenseQuantity: number;
  dispenseUnit: string;
  costCents: number;
  label?: string;
}) => post<{ cost: PharmacyCostRow }>('/payables/costs', body);
export const updatePharmacyCost = (id: string, costCents: number) =>
  patch<{ cost: PharmacyCostRow }>(`/payables/costs/${id}`, { costCents });
export const deletePharmacyCost = (id: string) => del<{ ok: true }>(`/payables/costs/${id}`);

/* ─── Public: forgot password / set password ────────────────── */
export const forgotPassword = (email: string) => post<{ ok: true }>('/auth/forgot', { email });
export const verifyResetToken = (token: string) =>
  get<{ ok: true; email: string; name: string }>(`/auth/set-password/${token}`);
export const setPassword = (token: string, password: string) =>
  post<{ ok: true }>('/auth/set-password', { token, password });
